import SpiceLevelBadge from '../../components/SpiceLevelBadge';
import MenuItemImage from '../../components/MenuItemImage';
import { SpiceLevel } from '../../types/menu';
import { DisplayContext, formatOrderId } from '../../utils/orderFormatter';
import { useTheme } from '../../context/ThemeContext';
import { CURRENCY } from '../../config/currency';

// Item styling for modern theme
const itemKeywordStyles: { [key: string]: { emoji: string; color: string } } = {
  coffee: { emoji: '☕', color: 'var(--status-warn-text)' },
  cappuccino: { emoji: '☕', color: '#6F4E37' }, /* basil-todo */
  latte: { emoji: '☕', color: '#C4A484' }, /* basil-todo */
  tea: { emoji: '🍵', color: '#228B22' }, /* basil-todo */
  juice: { emoji: '🧃', color: 'var(--status-warn)' },
  orange: { emoji: '🍊', color: 'var(--status-warn)' },
  smoothie: { emoji: '🥤', color: '#FF69B4' }, /* basil-todo */
  pizza: { emoji: '🍕', color: '#FF6347' }, /* basil-todo */
  burger: { emoji: '🍔', color: '#CD853F' }, /* basil-todo */
  sandwich: { emoji: '🥪', color: '#DAA520' }, /* basil-todo */
  pasta: { emoji: '🍝', color: 'var(--status-warn)' },
  noodles: { emoji: '🍜', color: '#FFD700' }, /* basil-todo */
  biryani: { emoji: '🍛', color: '#FF8C00' }, /* basil-todo */
  curry: { emoji: '🍛', color: '#B8860B' }, /* basil-todo */
  chicken: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  fish: { emoji: '🐟', color: '#4682B4' }, /* basil-todo */
  soup: { emoji: '🍲', color: '#FF7F50' }, /* basil-todo */
  salad: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  caesar: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  fries: { emoji: '🍟', color: '#FFD700' }, /* basil-todo */
  fry: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  cake: { emoji: '🍰', color: '#FF69B4' }, /* basil-todo */
  icecream: { emoji: '🍨', color: '#FFB6C1' }, /* basil-todo */
  bruschetta: { emoji: '🥖', color: '#CD853F' }, /* basil-todo */
  bread: { emoji: '🍞', color: '#D2691E' }, /* basil-todo */
  default: { emoji: '🍽️', color: 'var(--accent-alt)' }
};

const getItemEmoji = (itemName: string): { emoji: string; color: string } => {
  const lowerName = itemName.toLowerCase();
  for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
    if (keyword !== 'default' && lowerName.includes(keyword)) {
      return style;
    }
  }
  return itemKeywordStyles.default;
};

interface OrderItem {
  name: string;
  qty: number;
  price: number;
  requestedSpiceLevel?: SpiceLevel;
  portionName?: string;
}

interface Order {
  id: string;
  customOrderId?: string;
  date: string;
  status: string;
  total: number;
  vendor: string;
  items: OrderItem[];
  // Table info
  tableName?: string;
  floorName?: string;
  sectionName?: string;
  // Tax breakdown fields
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  taxType?: string;
}

interface OrderSummaryProps {
  order: Order;
}

export default function OrderSummary({ order }: OrderSummaryProps) {
  const { currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Order {formatOrderId(order.customOrderId || `#${order.id}`, DisplayContext.VENDOR)}
          {order.vendor && <span className="text-sm text-gray-500 font-normal ml-2">({order.vendor})</span>}
        </h3>
      </div>
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm text-gray-500">Date: {order.date}</p>
        <span
          className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
            order.status === "COMPLETED"
              ? "bg-green-100 text-green-700"
              : order.status === "PENDING"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {order.status}
        </span>
      </div>

      {/* Table Info */}
      {order.tableName && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 rounded-lg">
          <span className="text-lg">🍽️</span>
          <div className="text-sm">
            <span className="font-medium text-blue-800">Table {order.tableName}</span>
            {order.sectionName && <span className="text-blue-600"> • {order.sectionName}</span>}
            {order.floorName && <span className="text-blue-600"> • {order.floorName}</span>}
          </div>
        </div>
      )}
      
      <div className="space-y-3 text-gray-800 mb-4">
        {order.items.map((item, idx) => {
          const itemStyle = isModernTheme ? getItemEmoji(item.name) : null;
          return (
            <div 
              key={idx} 
              className={`pb-2 last:border-b-0 ${isModernTheme ? 'p-3 rounded-lg' : 'border-b'}`}
              style={isModernTheme && itemStyle ? {
                backgroundColor: `${itemStyle.color}08`,
                border: `1px solid ${itemStyle.color}20`
              } : {}}
            >
              <div className="flex justify-between items-start">
                <div className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    {(item as any).imageUrl ? (
                      <span className="inline-block w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                        <MenuItemImage imageUrl={(item as any).imageUrl} itemName={item.name} size="thumbnail" />
                      </span>
                    ) : isModernTheme && itemStyle ? (
                      <span className="text-xl">{itemStyle.emoji}</span>
                    ) : null}
                    <span className={isModernTheme ? "font-medium" : ""}>{`${item.qty}x ${item.name}`}</span>
                  </span>
                  {item.portionName && (
                    <span 
                      className="text-xs px-2 py-0.5 rounded-md inline-block w-fit font-medium" 
                      style={isModernTheme && itemStyle ? {
                        backgroundColor: `${itemStyle.color}20`,
                        color: itemStyle.color
                      } : { 
                        backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                        color: 'var(--text-link)' 
                      }}
                    >
                      {item.portionName}
                    </span>
                  )}
                  {item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE' && (
                    <div>
                      <SpiceLevelBadge spiceLevel={item.requestedSpiceLevel} size="small" />
                    </div>
                  )}
                </div>
                <span 
                  className="font-semibold"
                  style={isModernTheme && itemStyle ? { color: itemStyle.color } : {}}
                >
                  {CURRENCY.symbol}{item.price.toFixed(2)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="border-t pt-4">
        <div className="mb-2">
          <span className="text-sm text-gray-500">Vendor: {order.vendor}</span>
        </div>

        {/* Tax breakdown */}
        <div className="space-y-1 text-sm mb-3">
          {/* Subtotal */}
          {order.subtotal !== undefined && order.subtotal !== null && (
            <div className="flex justify-between">
              <span className="text-gray-500">Subtotal</span>
              <span className="text-gray-800">{CURRENCY.symbol}{order.subtotal.toFixed(2)}</span>
            </div>
          )}

          {/* Discount — before taxes/service charge: they are computed on the
              discounted base, so display order mirrors calculation order */}
          {order.discountAmount !== undefined && order.discountAmount !== null && order.discountAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Discount</span>
              <span className="text-green-600">-{CURRENCY.symbol}{order.discountAmount.toFixed(2)}</span>
            </div>
          )}

          {/* CGST */}
          {order.cgstAmount !== undefined && order.cgstAmount !== null && order.cgstAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">
                CGST {order.cgstRate ? `(${order.cgstRate}%)` : ''}
              </span>
              <span className="text-gray-800">{CURRENCY.symbol}{order.cgstAmount.toFixed(2)}</span>
            </div>
          )}

          {/* SGST */}
          {order.sgstAmount !== undefined && order.sgstAmount !== null && order.sgstAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">
                SGST {order.sgstRate ? `(${order.sgstRate}%)` : ''}
              </span>
              <span className="text-gray-800">{CURRENCY.symbol}{order.sgstAmount.toFixed(2)}</span>
            </div>
          )}

          {/* IGST */}
          {order.igstAmount !== undefined && order.igstAmount !== null && order.igstAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">
                IGST {order.igstRate ? `(${order.igstRate}%)` : ''}
              </span>
              <span className="text-gray-800">{CURRENCY.symbol}{order.igstAmount.toFixed(2)}</span>
            </div>
          )}

          {/* Service Charge */}
          {order.serviceChargeAmount !== undefined && order.serviceChargeAmount !== null && order.serviceChargeAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">
                Service Charge {order.serviceChargeRate ? `(${order.serviceChargeRate}%)` : ''}
              </span>
              <span className="text-gray-800">{CURRENCY.symbol}{order.serviceChargeAmount.toFixed(2)}</span>
            </div>
          )}

        </div>

        {/* Total */}
        <div className="border-t pt-2 flex justify-between items-center">
          <span className="text-base font-semibold text-gray-800">Total</span>
          <span className="text-lg font-semibold text-gray-800">
            {CURRENCY.symbol}{order.total.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}
